import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceStore } from "./use-workspace";
import { apiRequest } from "@/lib/queryClient";
import type { ContentItem } from "@shared/schema";

export function useContent() {
  const { activeWorkspaceId } = useWorkspaceStore();
  return useQuery<ContentItem[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "content"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/content`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch content");
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });
}

export function useCreateContent() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
    },
  });
}

export function useUpdateContent() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: number } & Partial<ContentItem>) => {
      const res = await apiRequest("PATCH", `/api/content/${id}`, updates);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
    },
  });
}

export function useApproveContent() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/content/${id}/approve`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
    },
  });
}

export function useRejectContent() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/content/${id}/reject`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
    },
  });
}

export function useDeleteContent() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/content/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
    },
  });
}
